<template>
  <div class="users-page">
    <el-row :gutter="16" class="stats-row">
      <el-col v-for="card in statCards" :key="card.key" :xs="24" :sm="12" :lg="6">
        <el-card shadow="hover" class="stat-card" :body-style="{ padding: '20px' }">
          <div class="stat-top">
            <div>
              <div class="stat-label">{{ card.label }}</div>
              <div class="stat-value">{{ card.value }}</div>
              <div v-if="card.extra" class="stat-extra">{{ card.extra }}</div>
            </div>
            <div class="stat-icon" :style="{ background: card.bg }">
              <i :class="card.icon" />
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="never" class="table-card">
      <div class="toolbar">
        <el-input
          v-model="keyword"
          placeholder="搜索账号名称、手机号或 ID..."
          clearable
          class="toolbar-search"
          @keyup.enter.native="handleSearch"
        >
          <el-button slot="append" icon="el-icon-search" @click="handleSearch" />
        </el-input>
        <el-select v-model="status" placeholder="全部状态" clearable class="toolbar-filter" @change="handleSearch">
          <el-option v-for="opt in statusOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
        </el-select>
        <el-select v-model="memberType" placeholder="全部类型" clearable class="toolbar-filter" @change="handleSearch">
          <el-option v-for="opt in memberTypeOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
        </el-select>
        <el-button type="primary" icon="el-icon-plus" @click="openCreate">注册账号</el-button>
      </div>

      <el-table v-loading="loading" :data="list" stripe class="user-table">
        <el-table-column label="账号" min-width="180">
          <template slot-scope="{ row }">
            <div class="user-cell">
              <div class="avatar" :style="{ background: avatarColor(row.id) }">
                {{ userInitial(row.nickname) }}
              </div>
              <div>
                <div class="nickname">{{ row.nickname || '-' }}</div>
                <div class="user-id">ID: {{ row.id }}</div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="绑定手机" min-width="120">
          <template slot-scope="{ row }">{{ row.phone || '-' }}</template>
        </el-table-column>
        <el-table-column label="用户类型" width="90">
          <template slot-scope="{ row }">
            <el-tag size="small" :type="row.memberType === 2 ? 'warning' : ''">
              {{ labelOf(MEMBER_TYPE, row.memberType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="门户会员" width="120">
          <template slot-scope="{ row }">
            <span :class="{ 'text-muted': !row.membershipTier || row.membershipTier === 'none' }">
              {{ membershipLevelLabel(row.membershipTier, row.membershipCycle) }}
            </span>
            <div class="bal-sub">{{ formatUserDate(row.membershipExpiresAt) || '无到期' }}</div>
          </template>
        </el-table-column>
        <el-table-column label="任务积分" width="150" align="left">
          <template slot-scope="{ row }">
            <button type="button" class="points-stat" @click="openLedger(row, 'task')">
              <div class="ps-line">剩余 {{ fmtPoints(row.taskBalance) }} 积分</div>
              <div class="ps-line muted">已用 {{ fmtPoints(row.taskUsed) }} 积分</div>
            </button>
          </template>
        </el-table-column>
        <el-table-column label="充值积分" width="150" align="left">
          <template slot-scope="{ row }">
            <button type="button" class="points-stat" @click="openLedger(row, 'recharge')">
              <div class="ps-line">剩余 {{ fmtPoints(row.universalBalance) }} 积分</div>
              <div class="ps-line muted">已用 {{ fmtPoints(row.universalUsed) }} 积分</div>
            </button>
          </template>
        </el-table-column>
        <el-table-column label="算力会员" width="130">
          <template slot-scope="{ row }">
            <template v-if="row.computeLinked">
              <span :class="{ 'text-muted': !row.computeMembershipTitle }">
                {{ row.computeMembershipTitle || '无订阅' }}
              </span>
              <div v-if="row.computeMembershipTitle" class="bal-sub">
                {{ formatUserDate(row.computeMembershipExpiresAt) || '无到期' }}
              </div>
            </template>
            <span v-else class="text-muted">未关联</span>
          </template>
        </el-table-column>
        <el-table-column label="算力任务积分" width="110" align="right">
          <template slot-scope="{ row }">
            <el-button
              v-if="row.computeLinked"
              type="text"
              class="points-link"
              @click="openLedger(row, 'task', 'compute')"
            >
              {{ row.computeTaskBalance != null ? row.computeTaskBalance : 0 }}
            </el-button>
            <span v-else class="text-muted">-</span>
          </template>
        </el-table-column>
        <el-table-column label="算力充值积分" width="110" align="right">
          <template slot-scope="{ row }">
            <el-button
              v-if="row.computeLinked"
              type="text"
              class="points-link"
              @click="openLedger(row, 'recharge', 'compute')"
            >
              {{ row.computeRechargeBalance != null ? row.computeRechargeBalance : 0 }}
            </el-button>
            <span v-else class="text-muted">-</span>
          </template>
        </el-table-column>
        <el-table-column label="技能标签" min-width="200">
          <template slot-scope="{ row }">
            <el-tag
              v-for="tag in parseSkillTags(row.skills).slice(0, 3)"
              :key="tag"
              size="mini"
              class="skill-tag"
            >
              {{ tag }}
            </el-tag>
            <span v-if="!parseSkillTags(row.skills).length" class="text-muted">-</span>
          </template>
        </el-table-column>
        <el-table-column label="注册时间" width="120">
          <template slot-scope="{ row }">{{ formatUserDate(row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="90">
          <template slot-scope="{ row }">
            <el-tag size="small" :type="statusTagType(Number(row.status))">
              {{ labelOf(PORTAL_USER_STATUS, Number(row.status)) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="测试号" width="90" align="center">
          <template slot-scope="{ row }">
            <el-switch
              :value="Boolean(row.testAccount)"
              :disabled="testToggleLoadingId === row.id"
              active-color="#e6a23c"
              @change="val => toggleTestAccount(row, val)"
            />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="560" fixed="right">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="openDetail(row)">查看</el-button>
            <el-button size="mini" type="warning" plain :disabled="Number(row.status) === 3" @click="openMemberAdjust(row)">修改</el-button>
            <el-button size="mini" type="info" plain @click="openLedger(row)">积分明细</el-button>
            <el-button size="mini" plain :disabled="Number(row.status) === 3" @click="resetPasswordToPhone(row)">重置密码</el-button>
            <el-button size="mini" plain :disabled="Number(row.status) === 3" @click="openPhone(row)">换绑手机</el-button>
            <el-button
              v-if="Number(row.status) !== 3"
              size="mini"
              :type="Number(row.status) === 0 ? 'success' : 'warning'"
              plain
              @click="toggleStatus(row)"
            >
              {{ Number(row.status) === 0 ? '激活' : '封停' }}
            </el-button>
            <el-button
              v-if="Number(row.status) !== 3"
              size="mini"
              type="danger"
              plain
              @click="onCancel(row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pagination"
        background
        layout="total, prev, pager, next"
        :current-page.sync="page"
        :page-size="pageSize"
        :total="total"
        @current-change="loadUsers"
      />
    </el-card>

    <el-dialog
      :visible.sync="detailVisible"
      width="520px"
      top="8vh"
      custom-class="user-detail-dialog"
      @closed="handleDetailClosed"
    >
      <div v-if="detail" slot="title" class="detail-title">
        <span class="detail-icon"><i class="el-icon-user-solid" /></span>
        <span>用户详情</span>
      </div>

      <div v-if="detail" v-loading="detailLoading" class="detail-body">
        <template v-if="detail.memberType === 1">
          <div class="profile-hero">
            <div class="profile-avatar" :style="{ background: avatarColor(detail.id) }">
              {{ userInitial(detail.nickname || detail.realName) }}
            </div>
            <div class="profile-main">
              <div class="profile-name">{{ detailDisplayName }}</div>
              <div class="profile-meta">
                {{ formatUserCode(detail.id) }} | {{ labelOf(MEMBER_TYPE, detail.memberType) }}
              </div>
              <div class="profile-badges">
                <span class="badge badge-success">{{ labelOf(PORTAL_USER_STATUS, detail.status) }}</span>
                <span v-if="isVipUser(detail)" class="badge badge-vip">VIP会员</span>
              </div>
            </div>
          </div>

          <div class="info-list">
            <div class="info-row">
              <span class="info-label">手机号</span>
              <span class="info-value">{{ maskPhone(detail.phone) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">注册时间</span>
              <span class="info-value">{{ formatUserDate(detail.createdAt) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">门户会员</span>
              <span class="info-value">
                {{ membershipLevelLabel(detail.membershipTier, detail.membershipCycle) }}
                · {{ formatUserDate(detail.membershipExpiresAt) || '无到期' }}
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">门户积分</span>
              <span class="info-value">{{ detail.pointBalance || 0 }}（任务{{ detail.taskBalance || 0 }} · 充值{{ detail.universalBalance || 0 }}）</span>
            </div>
            <div class="info-row">
              <span class="info-label">算力订阅</span>
              <span class="info-value">
                <template v-if="detail.computeLinked">
                  {{ detail.computeMembershipTitle || '无订阅' }}
                  <template v-if="detail.computeMembershipTitle">
                    · {{ formatUserDate(detail.computeMembershipExpiresAt) || '无到期' }}
                  </template>
                  · 积分 {{ detail.computePointBalance != null ? detail.computePointBalance : 0 }}
                  （任务{{ detail.computeTaskBalance || 0 }} · 充值{{ detail.computeRechargeBalance || 0 }}）
                  · UID {{ detail.computeUserId }}
                </template>
                <template v-else>未关联</template>
              </span>
            </div>
            <div class="info-row info-row-tags">
              <span class="info-label">技能标签</span>
              <div class="info-value">
                <span
                  v-for="tag in parseSkillTags(detail.skills)"
                  :key="tag"
                  class="skill-pill"
                >{{ tag }}</span>
                <span v-if="!parseSkillTags(detail.skills).length" class="text-muted">-</span>
              </div>
            </div>
          </div>

          <div class="stats-section">
            <div class="stats-title">数据统计</div>
            <div class="stats-grid">
              <div class="stat-box stat-blue">
                <div class="stat-num">{{ detail.demandCount || 0 }}</div>
                <div class="stat-name">发布需求</div>
              </div>
              <div class="stat-box stat-purple">
                <div class="stat-num">{{ detail.workCount || 0 }}</div>
                <div class="stat-name">发布作品</div>
              </div>
              <div class="stat-box stat-gold">
                <div class="stat-num">{{ detail.pointBalance || 0 }}</div>
                <div class="stat-name">门户积分</div>
              </div>
              <div class="stat-box stat-blue">
                <div class="stat-num">{{ detail.computeLinked ? (detail.computePointBalance || 0) : '-' }}</div>
                <div class="stat-name">算力积分</div>
                <div v-if="detail.computeLinked" class="stat-name" style="margin-top:4px;opacity:.75">
                  任务{{ detail.computeTaskBalance || 0 }} · 充值{{ detail.computeRechargeBalance || 0 }}
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <el-descriptions :column="1" border size="small">
            <el-descriptions-item label="昵称">{{ detail.nickname || '-' }}</el-descriptions-item>
            <el-descriptions-item label="手机号">{{ detail.phone }}</el-descriptions-item>
            <el-descriptions-item label="用户类型">{{ labelOf(MEMBER_TYPE, detail.memberType) }}</el-descriptions-item>
            <el-descriptions-item label="门户会员">{{ membershipLevelLabel(detail.membershipTier, detail.membershipCycle) }}</el-descriptions-item>
            <el-descriptions-item label="门户积分">{{ detail.pointBalance || 0 }}</el-descriptions-item>
            <el-descriptions-item label="算力订阅">
              <template v-if="detail.computeLinked">
                {{ detail.computeMembershipTitle || '无订阅' }}
                / 积分 {{ detail.computePointBalance || 0 }}
                （任务{{ detail.computeTaskBalance || 0 }} · 充值{{ detail.computeRechargeBalance || 0 }}）
                / UID {{ detail.computeUserId }}
              </template>
              <template v-else>未关联</template>
            </el-descriptions-item>
            <el-descriptions-item label="公司名称">{{ detail.companyName || '-' }}</el-descriptions-item>
            <el-descriptions-item label="真实姓名">{{ detail.realName || '-' }}</el-descriptions-item>
            <el-descriptions-item label="状态">{{ labelOf(PORTAL_USER_STATUS, detail.status) }}</el-descriptions-item>
            <el-descriptions-item label="注册时间">{{ formatUserDate(detail.createdAt) }}</el-descriptions-item>
          </el-descriptions>
        </template>
      </div>

      <div v-if="detail" slot="footer" class="detail-footer">
        <el-button @click="detailVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <el-dialog title="编辑用户" :visible.sync="editVisible" width="480px">
      <el-form ref="editForm" :model="editForm" label-width="80px">
        <el-form-item label="昵称">
          <el-input v-model="editForm.nickname" />
        </el-form-item>
        <el-form-item label="技能标签">
          <el-input v-model="editForm.skills" placeholder="多个标签用逗号分隔" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="editVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitEdit">保存</el-button>
      </div>
    </el-dialog>

    <el-dialog
      title="门户会员与积分调整"
      :visible.sync="memberAdjustVisible"
      width="640px"
      top="8vh"
      @closed="handleMemberAdjustClosed"
    >
      <div v-if="memberAdjustUser" class="member-adjust-head">
        <div>{{ memberAdjustUser.nickname || `用户${memberAdjustUser.id}` }}</div>
        <div class="member-adjust-sub">
          门户：{{ membershipLevelLabel(memberAdjustUser.membershipTier, memberAdjustUser.membershipCycle) }}
          · 积分 {{ memberAdjustUser.pointBalance != null ? memberAdjustUser.pointBalance : 0 }}
        </div>
        <div class="member-adjust-sub">
          算力订阅：
          <template v-if="memberAdjustUser.computeLinked">
            {{ memberAdjustUser.computeMembershipTitle || '无订阅' }}
            · 积分 {{ memberAdjustUser.computePointBalance != null ? memberAdjustUser.computePointBalance : 0 }}
            （任务{{ memberAdjustUser.computeTaskBalance || 0 }} · 充值{{ memberAdjustUser.computeRechargeBalance || 0 }}）
            （UID {{ memberAdjustUser.computeUserId }}，请在算力中心后台调整）
          </template>
          <template v-else>未关联</template>
        </div>
      </div>
      <el-tabs v-model="memberAdjustTab">
        <el-tab-pane label="门户会员等级" name="tier">
          <el-form label-width="90px" size="small">
            <el-form-item label="会员等级">
              <el-select v-model="tierForm.tier" placeholder="选择等级" style="width: 220px" @change="handleTierChange">
                <el-option v-for="opt in tierOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
              </el-select>
            </el-form-item>
            <el-form-item v-if="tierForm.tier && tierForm.tier !== 'none'" label="计费周期">
              <el-select v-model="tierForm.cycle" placeholder="选择月费/年费" style="width: 220px">
                <el-option v-for="opt in cycleOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
              </el-select>
            </el-form-item>
            <el-form-item label="备注">
              <el-input v-model="tierForm.remark" type="textarea" :rows="2" placeholder="调整原因（可选）" />
            </el-form-item>
          </el-form>
        </el-tab-pane>
        <el-tab-pane label="门户积分" name="points">
          <el-form label-width="90px" size="small">
            <el-form-item label="积分变动">
              <el-input v-model="pointsForm.delta" placeholder="正数赠送如 100，负数扣减如 -50" style="width: 280px" />
            </el-form-item>
            <el-form-item label="备注" required>
              <el-input v-model="pointsForm.remark" type="textarea" :rows="2" placeholder="调整原因（必填）" />
            </el-form-item>
          </el-form>
        </el-tab-pane>
        <el-tab-pane label="门户到期" name="expire">
          <el-form label-width="90px" size="small">
            <el-form-item label="到期时间">
              <el-date-picker
                v-model="expireForm.expiresAt"
                type="datetime"
                value-format="yyyy-MM-dd HH:mm:ss"
                placeholder="选择到期时间"
                style="width: 280px"
              />
            </el-form-item>
            <el-form-item label="备注">
              <el-input v-model="expireForm.remark" type="textarea" :rows="2" placeholder="调整原因（可选）" />
            </el-form-item>
          </el-form>
        </el-tab-pane>
        <el-tab-pane label="变动记录" name="logs">
          <el-table v-loading="changeLogLoading" :data="changeLogList" border stripe size="small" max-height="320">
            <el-table-column label="时间" width="165">
              <template slot-scope="{ row }">{{ formatLedgerTime(row.createdAt) }}</template>
            </el-table-column>
            <el-table-column prop="changeTypeLabel" label="类型" width="90" />
            <el-table-column label="变更前" min-width="90" show-overflow-tooltip prop="oldValue" />
            <el-table-column label="变更后" min-width="90" show-overflow-tooltip prop="newValue" />
            <el-table-column label="变动" width="80" align="right">
              <template slot-scope="{ row }">
                <span v-if="row.deltaAmount != null" :class="ledgerAmountClass(row.deltaAmount)">
                  {{ formatLedgerAmount(row.deltaAmount) }}
                </span>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column prop="remark" label="备注" min-width="120" show-overflow-tooltip />
            <el-table-column prop="adminName" label="操作人" width="90" show-overflow-tooltip />
          </el-table>
          <el-pagination
            class="change-log-pagination"
            background
            layout="total, prev, pager, next"
            :current-page.sync="changeLogPage"
            :page-size="changeLogPageSize"
            :total="changeLogTotal"
            @current-change="loadChangeLogs"
          />
        </el-tab-pane>
      </el-tabs>
      <div slot="footer" class="member-adjust-footer">
        <el-button v-if="memberAdjustTab === 'tier'" type="primary" :loading="memberAdjustSubmitting" @click="submitTierAdjust">
          保存会员等级
        </el-button>
        <el-button v-if="memberAdjustTab === 'points'" type="primary" :loading="memberAdjustSubmitting" @click="submitPointsAdjust">
          确认调整
        </el-button>
        <el-button v-if="memberAdjustTab === 'expire'" type="primary" :loading="memberAdjustSubmitting" @click="submitExpireAdjust">
          保存到期时间
        </el-button>
        <el-button @click="memberAdjustVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <el-dialog title="注册账号" :visible.sync="createVisible" width="480px">
      <el-form label-width="90px" size="small">
        <el-form-item label="手机号" required>
          <el-input v-model="createForm.phone" maxlength="11" placeholder="11 位手机号" />
        </el-form-item>
        <el-form-item label="初始密码" required>
          <el-input v-model="createForm.password" show-password placeholder="至少 6 位" />
        </el-form-item>
        <el-form-item label="账号名称">
          <el-input v-model="createForm.nickname" placeholder="默认：创作者" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="createForm.remark" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="createVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitCreate">创建</el-button>
      </div>
    </el-dialog>

    <el-dialog title="换绑手机" :visible.sync="phoneVisible" width="440px">
      <el-form label-width="90px" size="small">
        <el-form-item label="新手机号" required>
          <el-input v-model="phoneForm.phone" maxlength="11" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="phoneForm.remark" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="phoneVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitPhone">保存</el-button>
      </div>
    </el-dialog>

    <el-dialog
      :visible.sync="ledgerVisible"
      width="860px"
      top="6vh"
      custom-class="ledger-dialog"
      @closed="handleLedgerClosed"
    >
      <div slot="title" class="ledger-title">
        <span>{{ ledgerTitle }}</span>
        <span v-if="ledgerUser" class="ledger-sub">
          {{ ledgerUser.nickname || `用户${ledgerUser.id}` }}
          <template v-if="ledgerSource === 'compute'">
            <template v-if="ledgerPool === 'task'">
              · 算力任务积分 {{ ledgerUser.computeTaskBalance != null ? ledgerUser.computeTaskBalance : 0 }}
            </template>
            <template v-else-if="ledgerPool === 'recharge'">
              · 算力充值积分 {{ ledgerUser.computeRechargeBalance != null ? ledgerUser.computeRechargeBalance : 0 }}
            </template>
            <template v-else>
              · 算力余额 {{ ledgerUser.computePointBalance != null ? ledgerUser.computePointBalance : 0 }}
            </template>
          </template>
          <template v-else>
            <template v-if="ledgerPool === 'task'">
              · 任务积分 {{ ledgerUser.taskBalance != null ? ledgerUser.taskBalance : 0 }}
            </template>
            <template v-else-if="ledgerPool === 'recharge'">
              · 充值积分 {{ ledgerUser.universalBalance != null ? ledgerUser.universalBalance : 0 }}
            </template>
            <template v-else>
              · 当前余额 {{ ledgerUser.pointBalance != null ? ledgerUser.pointBalance : 0 }}
            </template>
          </template>
        </span>
      </div>

      <div class="ledger-filter">
        <el-radio-group v-model="ledgerPool" size="mini" @change="onLedgerPoolChange">
          <el-radio-button label="all">全部</el-radio-button>
          <el-radio-button label="task">任务积分</el-radio-button>
          <el-radio-button label="recharge">充值积分</el-radio-button>
        </el-radio-group>
      </div>

      <el-table v-loading="ledgerLoading" :data="filteredLedgerList" stripe border size="small" max-height="420">
        <el-table-column label="时间" width="170">
          <template slot-scope="{ row }">{{ formatLedgerTime(row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="类型" width="90">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="ledgerTagType(row)">{{ ledgerTypeLabel(row) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="描述" min-width="220" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ formatLedgerDescription(row) }}</template>
        </el-table-column>
        <el-table-column label="变动" width="100" align="right">
          <template slot-scope="{ row }">
            <span :class="ledgerAmountClass(row.amount)">{{ formatLedgerAmount(row.amount) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="余额" width="100" align="right">
          <template slot-scope="{ row }">{{ row.balanceAfter != null ? row.balanceAfter : '-' }}</template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="ledger-pagination"
        background
        layout="total, prev, pager, next"
        :current-page.sync="ledgerPage"
        :page-size="ledgerPageSize"
        :total="ledgerTotal"
        @current-change="loadLedger"
      />

      <div slot="footer">
        <el-button @click="ledgerVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getUsers,
  getUserStats,
  getUserDetail,
  getUserCreditsLedger,
  getUserComputeCreditsLedger,
  getUserAdminChanges,
  createUser,
  cancelUser,
  resetUserPassword,
  updateUserPhone,
  updateUser,
  updateUserStatus,
  updateUserTestAccount,
  updateUserMembershipTier,
  updateUserMembershipExpire,
  adjustUserPoints
} from '@/api/admin'
import {
  formatCreditsLedgerCopy,
  ledgerTypeLabel,
  ledgerRowPool as resolveLedgerRowPool,
  collapseSettledFreezeRows
} from '@/utils/creditsLedger'
import { MEMBER_TYPE, MEMBERSHIP_TIER, PORTAL_USER_STATUS, labelOf, membershipCycleOptions, membershipLevelLabel, optionsOf } from '@/utils/adminConstants'
import {
  avatarColor,
  formatPercent,
  formatUserCode,
  formatUserDate,
  maskPhone,
  parseSkillTags,
  userInitial
} from '@/utils/userFormat'
import { formatCount } from '@/utils/dashboardFormat'
import { formatShanghaiDateTime } from '@/utils/datetime'

export default {
  name: 'AdminUsers',
  data() {
    return {
      MEMBER_TYPE,
      PORTAL_USER_STATUS,
      loading: false,
      submitting: false,
      list: [],
      total: 0,
      page: 1,
      pageSize: 10,
      keyword: '',
      memberType: null,
      status: null,
      stats: {},
      detailVisible: false,
      detailLoading: false,
      editVisible: false,
      detail: null,
      editForm: { id: null, nickname: '', skills: '' },
      ledgerVisible: false,
      ledgerLoading: false,
      ledgerUser: null,
      ledgerList: [],
      ledgerTotal: 0,
      ledgerPage: 1,
      ledgerPageSize: 20,
      /** all | task | recharge */
      ledgerPool: 'all',
      /** portal | compute */
      ledgerSource: 'portal',
      memberAdjustVisible: false,
      memberAdjustSubmitting: false,
      memberAdjustTab: 'tier',
      memberAdjustUser: null,
      tierForm: { tier: 'none', cycle: 'monthly', remark: '' },
      pointsForm: { delta: '', remark: '' },
      expireForm: { expiresAt: '', remark: '' },
      createVisible: false,
      createForm: { phone: '', password: '', nickname: '', remark: '' },
      phoneVisible: false,
      phoneForm: { id: null, phone: '', remark: '' },
      changeLogLoading: false,
      changeLogList: [],
      changeLogTotal: 0,
      changeLogPage: 1,
      changeLogPageSize: 10,
      testToggleLoadingId: null
    }
  },
  computed: {
    tierOptions() {
      return [{ value: 'none', label: '无会员' }].concat(
        Object.keys(MEMBERSHIP_TIER).map(key => ({ value: key, label: MEMBERSHIP_TIER[key] }))
      )
    },
    cycleOptions() {
      return membershipCycleOptions()
    },
    statusOptions() {
      return optionsOf(PORTAL_USER_STATUS)
    },
    memberTypeOptions() {
      return optionsOf(MEMBER_TYPE)
    },
    detailDisplayName() {
      if (!this.detail) return '-'
      return this.detail.nickname || this.detail.realName || `用户${this.detail.id}`
    },
    statCards() {
      const total = Number(this.stats.totalCount || 0)
      return [
        {
          key: 'total',
          label: '总注册用户',
          value: formatCount(this.stats.totalCount),
          extra: `+${formatCount(this.stats.monthNewCount)} 本月新增`,
          icon: 'el-icon-user-solid',
          bg: 'rgba(64, 158, 255, 0.12)'
        },
        {
          key: 'personal',
          label: '个人创作者',
          value: formatCount(this.stats.personalCount),
          extra: `占比 ${formatPercent(this.stats.personalCount, total)}`,
          icon: 'el-icon-s-custom',
          bg: 'rgba(245, 158, 11, 0.12)'
        },
        {
          key: 'company',
          label: '企业用户',
          value: formatCount(this.stats.companyCount),
          extra: `占比 ${formatPercent(this.stats.companyCount, total)}`,
          icon: 'el-icon-office-building',
          bg: 'rgba(139, 92, 246, 0.12)'
        },
        {
          key: 'agent',
          label: '代理商',
          value: formatCount(this.stats.agentCount),
          extra: `占比 ${formatPercent(this.stats.agentCount, total)}`,
          icon: 'el-icon-s-flag',
          bg: 'rgba(16, 185, 129, 0.12)'
        }
      ]
    },
    ledgerTitle() {
      const prefix = this.ledgerSource === 'compute' ? '算力' : ''
      if (this.ledgerPool === 'task') return `${prefix}任务积分明细`
      if (this.ledgerPool === 'recharge') return `${prefix}充值积分明细`
      return `${prefix}积分明细`
    },
    filteredLedgerList() {
      const rows = this.ledgerList || []
      if (this.ledgerPool === 'all') return rows
      return rows.filter((row) => this.ledgerRowPool(row) === this.ledgerPool)
    }
  },
  created() {
    this.loadStats()
    this.loadUsers()
  },
  methods: {
    labelOf,
    maskPhone,
    formatUserCode,
    formatUserDate,
    parseSkillTags,
    userInitial,
    avatarColor,
    membershipLevelLabel,
    isVipUser(detail) {
      return Number(detail.pointBalance || 0) >= 1000
    },
    handleDetailClosed() {
      this.detail = null
    },
    handleLedgerClosed() {
      this.ledgerUser = null
      this.ledgerList = []
      this.ledgerTotal = 0
      this.ledgerPage = 1
      this.ledgerPool = 'all'
      this.ledgerSource = 'portal'
    },
    handleMemberAdjustClosed() {
      this.memberAdjustUser = null
      this.memberAdjustTab = 'tier'
      this.tierForm = { tier: 'none', cycle: 'monthly', remark: '' }
      this.pointsForm = { delta: '', remark: '' }
      this.expireForm = { expiresAt: '', remark: '' }
      this.changeLogList = []
      this.changeLogTotal = 0
      this.changeLogPage = 1
    },
    openMemberAdjust(row) {
      this.memberAdjustUser = { ...row }
      this.tierForm = {
        tier: row.membershipTier || 'none',
        cycle: row.membershipCycle || 'monthly',
        remark: ''
      }
      this.pointsForm = { delta: '', remark: '' }
      const expireText = row.membershipExpiresAt
        ? formatShanghaiDateTime(row.membershipExpiresAt)
        : ''
      this.expireForm = {
        expiresAt: expireText && expireText !== '-' ? expireText : '',
        remark: ''
      }
      this.memberAdjustTab = 'tier'
      this.memberAdjustVisible = true
      this.changeLogPage = 1
      this.loadChangeLogs()
    },
    openCreate() {
      this.createForm = { phone: '', password: '', nickname: '', remark: '' }
      this.createVisible = true
    },
    async submitCreate() {
      const phone = String(this.createForm.phone || '').trim()
      const password = String(this.createForm.password || '').trim()
      if (!/^1\d{10}$/.test(phone)) {
        this.$message.warning('请输入正确手机号')
        return
      }
      if (password.length < 6) {
        this.$message.warning('密码至少 6 位')
        return
      }
      this.submitting = true
      try {
        await createUser({
          phone,
          password,
          nickname: this.createForm.nickname || undefined,
          remark: this.createForm.remark || undefined
        })
        this.$message.success('账号已创建')
        this.createVisible = false
        this.loadUsers()
        this.loadStats()
      } catch (e) {
        this.$message.error(e.message || '创建失败')
      } finally {
        this.submitting = false
      }
    },
    resetPasswordToPhone(row) {
      const phone = String(row.phone || '').trim()
      if (!/^1\d{10}$/.test(phone)) {
        this.$message.warning('该用户未绑定有效手机号，无法重置密码')
        return
      }
      const name = row.nickname || row.phone || ('ID ' + row.id)
      this.$confirm(`确认将账号「${name}」的密码重置为手机号 ${phone}？`, '重置密码', {
        type: 'warning',
        confirmButtonText: '确认重置',
        cancelButtonText: '取消'
      }).then(async () => {
        this.submitting = true
        try {
          await resetUserPassword(row.id, { password: phone, remark: '后台重置密码为手机号' })
          this.$message.success('密码已重置为手机号')
        } catch (e) {
          this.$message.error(e.message || '重置失败')
        } finally {
          this.submitting = false
        }
      }).catch(() => {})
    },
    openPhone(row) {
      this.phoneForm = { id: row.id, phone: row.phone || '', remark: '' }
      this.phoneVisible = true
    },
    async submitPhone() {
      const phone = String(this.phoneForm.phone || '').trim()
      if (!/^1\d{10}$/.test(phone)) {
        this.$message.warning('请输入正确手机号')
        return
      }
      this.submitting = true
      try {
        const updated = await updateUserPhone(this.phoneForm.id, {
          phone,
          remark: this.phoneForm.remark || undefined
        })
        this.$message.success('手机号已更新')
        this.phoneVisible = false
        this.syncRow(updated)
      } catch (e) {
        this.$message.error(e.message || '换绑失败')
      } finally {
        this.submitting = false
      }
    },
    onCancel(row) {
      this.$confirm(
        `确认删除账号 ${row.nickname || row.phone || row.id}？将软删除并保留历史记录，原手机号改为「ID+手机」以便重新注册；算力侧同步软删。`,
        '删除账号',
        { type: 'warning' }
      ).then(async () => {
        try {
          const updated = await cancelUser(row.id, { remark: '管理后台删除（双侧软删）' })
          this.$message.success('已删除（门户+算力）')
          this.syncRow(updated)
        } catch (e) {
          this.$message.error(e.message || '删除失败')
        }
      }).catch(() => {})
    },
    async submitExpireAdjust() {
      if (!this.memberAdjustUser) return
      this.memberAdjustSubmitting = true
      try {
        const updated = await updateUserMembershipExpire(this.memberAdjustUser.id, {
          expiresAt: this.expireForm.expiresAt || null,
          remark: this.expireForm.remark || undefined
        })
        this.$message.success('到期时间已更新')
        this.memberAdjustUser = { ...this.memberAdjustUser, ...updated }
        this.syncRow(updated)
        this.loadChangeLogs()
      } catch (e) {
        this.$message.error(e.message || '更新失败')
      } finally {
        this.memberAdjustSubmitting = false
      }
    },
    async loadChangeLogs() {
      if (!this.memberAdjustUser) return
      this.changeLogLoading = true
      try {
        const result = await getUserAdminChanges(this.memberAdjustUser.id, {
          page: this.changeLogPage,
          size: this.changeLogPageSize
        })
        this.changeLogList = result.list || []
        this.changeLogTotal = Number(result.total || 0)
      } catch (e) {
        this.$message.error(e.message || '加载变动记录失败')
        this.changeLogList = []
        this.changeLogTotal = 0
      } finally {
        this.changeLogLoading = false
      }
    },
    handleTierChange(tier) {
      if (!tier || tier === 'none') {
        this.tierForm.cycle = 'monthly'
      } else if (!this.tierForm.cycle) {
        this.tierForm.cycle = 'monthly'
      }
    },
    async submitTierAdjust() {
      if (!this.memberAdjustUser) return
      if (this.tierForm.tier !== 'none' && !this.tierForm.cycle) {
        this.$message.warning('请选择月费或年费')
        return
      }
      this.memberAdjustSubmitting = true
      try {
        const updated = await updateUserMembershipTier(this.memberAdjustUser.id, {
          tier: this.tierForm.tier,
          cycle: this.tierForm.tier === 'none' ? undefined : this.tierForm.cycle,
          remark: this.tierForm.remark || undefined
        })
        this.$message.success('会员等级已更新')
        this.memberAdjustUser = { ...this.memberAdjustUser, ...updated }
        this.syncRow(updated)
        this.tierForm.remark = ''
        this.loadChangeLogs()
      } catch (e) {
        this.$message.error(e.message || '更新失败')
      } finally {
        this.memberAdjustSubmitting = false
      }
    },
    async submitPointsAdjust() {
      if (!this.memberAdjustUser) return
      const raw = String(this.pointsForm.delta || '').trim()
      const delta = Number(raw)
      if (!raw || Number.isNaN(delta) || !Number.isInteger(delta) || delta === 0) {
        this.$message.warning('请输入非 0 整数（正数赠送，负数扣减）')
        return
      }
      const balance = Number(this.memberAdjustUser.pointBalance != null ? this.memberAdjustUser.pointBalance : 0)
      if (delta < 0 && balance + delta < 0) {
        this.$message.warning(`扣减后余额不能为负，当前余额 ${balance}`)
        return
      }
      const remark = String(this.pointsForm.remark || '').trim()
      if (!remark) {
        this.$message.warning('请填写调整备注')
        return
      }
      this.memberAdjustSubmitting = true
      try {
        const updated = await adjustUserPoints(this.memberAdjustUser.id, {
          delta,
          remark
        })
        this.$message.success(delta > 0 ? '积分已赠送' : '积分已扣减')
        this.memberAdjustUser = { ...this.memberAdjustUser, ...updated }
        this.syncRow(updated)
        this.pointsForm = { delta: '', remark: '' }
        this.loadChangeLogs()
      } catch (e) {
        this.$message.error(e.message || '调整失败')
      } finally {
        this.memberAdjustSubmitting = false
      }
    },
    syncRow(updated) {
      if (!updated || !updated.id) return
      const idx = this.list.findIndex(item => item.id === updated.id)
      if (idx >= 0) {
        this.$set(this.list, idx, { ...this.list[idx], ...updated })
      }
    },
    ledgerTypeLabel,
    formatLedgerTime(value) {
      return formatShanghaiDateTime(value)
    },
    formatLedgerDescription(row) {
      const copy = formatCreditsLedgerCopy(row)
      if (copy.description) return `${copy.title} · ${copy.description}`
      return copy.title
    },
    formatLedgerAmount(amount) {
      const n = Number(amount) || 0
      if (n > 0) return `+${n}`
      return String(n)
    },
    ledgerAmountClass(amount) {
      const n = Number(amount) || 0
      if (n > 0) return 'ledger-plus'
      if (n < 0) return 'ledger-minus'
      return ''
    },
    ledgerTagType(row) {
      const label = ledgerTypeLabel(row)
      if (/消耗|过期/.test(label)) return 'danger'
      if (/预扣|占用/.test(label)) return 'warning'
      if (/任务积分|充值积分|会员积分|赠送|回退/.test(label)) return 'success'
      const key = String((row && row.transactionType) || row || '').toUpperCase()
      if (key === 'TOPUP' || key === 'UNFREEZE') return 'success'
      if (key === 'CONSUME') return 'danger'
      if (key === 'FREEZE' || key === 'HOLDING') return 'warning'
      return 'info'
    },
    fmtPoints(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return '0'
      return Math.round(n).toLocaleString('zh-CN')
    },
    openLedger(row, pool = 'all', source = 'portal') {
      this.ledgerUser = { ...row }
      this.ledgerSource = source === 'compute' ? 'compute' : 'portal'
      this.ledgerPool = pool === 'task' || pool === 'recharge' ? pool : 'all'
      this.ledgerVisible = true
      this.ledgerPage = 1
      this.loadLedger()
    },
    onLedgerPoolChange() {
      // 客户端按池过滤；明细已一次性拉取
    },
    /** 流水归属：任务积分 / 充值积分（与创作中心池规则对齐） */
    ledgerRowPool(row) {
      const pool = resolveLedgerRowPool(row)
      if (pool === 'task' || pool === 'recharge') return pool
      // 无法判定池的消费：筛选任务/充值 Tab 时仍展示，避免漏单
      if (String(row?.transactionType || '').toUpperCase() === 'CONSUME') {
        return this.ledgerPool === 'all' ? 'all' : this.ledgerPool
      }
      return 'all'
    },
    async loadLedger() {
      if (!this.ledgerUser) return
      this.ledgerLoading = true
      try {
        const fetcher = this.ledgerSource === 'compute'
          ? getUserComputeCreditsLedger
          : getUserCreditsLedger
        // 拉取更大页便于按任务/充值池客户端过滤
        const result = await fetcher(this.ledgerUser.id, {
          page: this.ledgerPage,
          size: Math.max(this.ledgerPageSize, 100)
        })
        const list = result.list || []
        this.ledgerList = this.ledgerSource === 'compute'
          ? list
          : collapseSettledFreezeRows(list)
        this.ledgerTotal = Number(result.total || 0)
      } catch (e) {
        this.$message.error(e.message || '加载积分明细失败')
        this.ledgerList = []
        this.ledgerTotal = 0
      } finally {
        this.ledgerLoading = false
      }
    },
    statusTagType(status) {
      if (status === 1) return 'success'
      if (status === 2) return 'warning'
      return 'info'
    },
    async loadStats() {
      try {
        this.stats = await getUserStats() || {}
      } catch (e) {
      }
    },
    handleSearch() {
      this.page = 1
      this.loadUsers()
    },
    async loadUsers() {
      this.loading = true
      try {
        const result = await getUsers({
          page: this.page,
          size: this.pageSize,
          keyword: this.keyword || undefined,
          memberType: this.memberType,
          status: this.status
        })
        this.list = result.list || []
        this.total = result.total || 0
      } catch (e) {
      } finally {
        this.loading = false
      }
    },
    async openDetail(row) {
      this.detailVisible = true
      this.detailLoading = true
      try {
        this.detail = await getUserDetail(row.id)
      } catch (e) {
        this.detailVisible = false
      } finally {
        this.detailLoading = false
      }
    },
    openEdit(row) {
      this.editForm = {
        id: row.id,
        nickname: row.nickname || '',
        skills: row.skills || ''
      }
      this.editVisible = true
    },
    async submitEdit() {
      this.submitting = true
      try {
        await updateUser(this.editForm.id, {
          nickname: this.editForm.nickname,
          skills: this.editForm.skills
        })
        this.$message.success('保存成功')
        this.editVisible = false
        this.loadUsers()
        this.loadStats()
      } catch (e) {
      } finally {
        this.submitting = false
      }
    },
    toggleStatus(row) {
      const current = Number(row.status)
      const nextStatus = current === 0 ? 1 : 0
      const action = nextStatus === 0 ? '封停' : '激活'
      const name = row.nickname || row.phone || `用户${row.id}`
      this.$confirm(`确定${action}用户「${name}」？门户与算力将同步${action}。`, '提示', { type: 'warning' })
        .then(async () => {
          await updateUserStatus(row.id, nextStatus)
          this.$message.success(`已${action}`)
          this.loadUsers()
        })
        .catch(err => {
          if (err === 'cancel' || err === 'close') return
        })
    },
    toggleTestAccount(row, enabled) {
      const name = row.nickname || row.phone || `用户${row.id}`
      const action = enabled ? '设为测试号' : '取消测试号'
      this.$confirm(
        `${action}后，「${name}」的消费将备注（测试）且实收为 0，火山成本照常统计。确定继续？`,
        '测试号设置',
        { type: 'warning' }
      )
        .then(async () => {
          this.testToggleLoadingId = row.id
          try {
            const updated = await updateUserTestAccount(row.id, {
              testAccount: enabled,
              remark: enabled ? '后台设为测试号' : '后台取消测试号'
            })
            this.$message.success(enabled ? '已设为测试号' : '已取消测试号')
            this.syncRow(updated)
          } catch (e) {
            this.$message.error(e.message || '操作失败')
          } finally {
            this.testToggleLoadingId = null
          }
        })
        .catch(err => {
          if (err === 'cancel' || err === 'close') return
        })
    }
  }
}
</script>

<style scoped>
.users-page {
  padding: 8px;
}
.stats-row {
  margin-bottom: 16px;
}
.stat-card {
  margin-bottom: 16px;
  border: none;
}
.stat-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.stat-label {
  color: #909399;
  font-size: 14px;
  margin-bottom: 8px;
}
.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
}
.stat-extra {
  margin-top: 8px;
  font-size: 13px;
  color: #10b981;
}
.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: #1e3a5f;
}
.table-card {
  border: none;
}
.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}
.toolbar-search {
  width: 320px;
  max-width: 100%;
}
.toolbar-filter {
  width: 140px;
}
.user-table {
  width: 100%;
}
.user-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}
.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: #fff;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.nickname {
  font-weight: 600;
  color: #303133;
}
.user-id {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}
.bal-sub {
  font-size: 11px;
  color: #94a3b8;
  line-height: 1.2;
  margin-top: 2px;
}
.skill-tag {
  margin-right: 6px;
  margin-bottom: 4px;
}
.text-muted {
  color: #c0c4cc;
}
.pagination {
  margin-top: 16px;
  text-align: right;
}
</style>

<style>
.user-detail-dialog .el-dialog__header {
  padding: 20px 24px 16px;
  border-bottom: 1px solid #f0f2f5;
}
.user-detail-dialog .el-dialog__body {
  padding: 20px 24px 8px;
}
.user-detail-dialog .el-dialog__footer {
  padding: 12px 24px 20px;
  border-top: 1px solid #f0f2f5;
}
.user-detail-dialog .detail-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}
.user-detail-dialog .detail-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(245, 158, 11, 0.15);
  color: #f59e0b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}
.user-detail-dialog .profile-hero {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}
.user-detail-dialog .profile-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  color: #fff;
  font-size: 28px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.user-detail-dialog .profile-name {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}
.user-detail-dialog .profile-meta {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 10px;
}
.user-detail-dialog .profile-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.user-detail-dialog .badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
}
.user-detail-dialog .badge-success {
  background: #ecfdf5;
  color: #10b981;
}
.user-detail-dialog .badge-vip {
  background: #eff6ff;
  color: #3b82f6;
}
.user-detail-dialog .info-list {
  border-top: 1px solid #f0f2f5;
  padding-top: 4px;
}
.user-detail-dialog .info-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #f8fafc;
}
.user-detail-dialog .info-label {
  flex-shrink: 0;
  width: 72px;
  font-size: 14px;
  color: #64748b;
}
.user-detail-dialog .info-value {
  flex: 1;
  text-align: right;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  word-break: break-all;
}
.user-detail-dialog .info-row-tags .info-value {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}
.user-detail-dialog .skill-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  color: #ec4899;
  background: #fdf2f8;
  border: 1px solid #fbcfe8;
}
.user-detail-dialog .stats-section {
  margin-top: 20px;
}
.user-detail-dialog .stats-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
}
.user-detail-dialog .stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.user-detail-dialog .stat-box {
  border-radius: 10px;
  padding: 14px 8px;
  text-align: center;
}
.user-detail-dialog .stat-num {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
}
.user-detail-dialog .stat-name {
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
}
.user-detail-dialog .stat-blue {
  background: #eff6ff;
}
.user-detail-dialog .stat-blue .stat-num {
  color: #3b82f6;
}
.user-detail-dialog .stat-purple {
  background: #f5f3ff;
}
.user-detail-dialog .stat-purple .stat-num {
  color: #8b5cf6;
}
.user-detail-dialog .stat-gold {
  background: #fffbeb;
}
.user-detail-dialog .stat-gold .stat-num {
  color: #f59e0b;
}
.user-detail-dialog .detail-footer {
  text-align: right;
}
.points-link {
  padding: 0;
  font-weight: 600;
  color: #f59e0b;
}
.points-stat {
  display: block;
  width: 100%;
  margin: 0;
  padding: 2px 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
  color: #303133;
  line-height: 1.35;
}
.points-stat:hover .ps-line {
  color: #f59e0b;
}
.points-stat .ps-line {
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}
.points-stat .ps-line.muted {
  color: #909399;
  font-weight: 400;
}
.ledger-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ledger-sub {
  font-size: 12px;
  font-weight: normal;
  color: #64748b;
}
.ledger-filter {
  margin: 0 0 12px;
}
.ledger-pagination {
  margin-top: 16px;
  text-align: right;
}
.ledger-plus {
  color: #16a34a;
  font-weight: 600;
}
.ledger-minus {
  color: #dc2626;
  font-weight: 600;
}
.member-adjust-head {
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-size: 14px;
  color: #334155;
}
.member-adjust-sub {
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
}
.member-adjust-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
}
.change-log-pagination {
  margin-top: 12px;
  text-align: right;
}
</style>
